import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errorMessage } from "../lib/api";
import { Switch } from "./ui/switch";

// « Ce que je reçois » (Ordre 24-2): one line per category, two channels (the bell, WhatsApp). The administrator's own settings stay above
// these: an event switched off for the company never reaches anybody, whatever is chosen here.
export default function NotificationChoices() {
  const [state, setState] = useState({ loading: true, error: false, categories: [], whatsappMaster: true });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/preferences");
      setState({ loading: false, error: false, categories: data.categories, whatsappMaster: data.whatsapp_master });
    } catch (e) {
      setState((prev) => ({ ...prev, loading: false, error: true }));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const change = async (id, channel, value) => {
    const previous = state.categories;
    const categories = state.categories.map((c) => (c.id === id ? { ...c, [channel]: value } : c));
    setState((prev) => ({ ...prev, categories }));
    setSaving(true);
    try {
      await api.put("/notifications/preferences", { prefs: Object.fromEntries(categories.map((c) => [c.id, { bell: c.bell, whatsapp: c.whatsapp }])) });
    } catch (e) {
      // The switch goes back to what the server holds: a choice that was not saved must not look saved.
      setState((prev) => ({ ...prev, categories: previous }));
      toast.error(errorMessage(e, "Impossible d'enregistrer ce choix. Réessayez dans quelques instants."));
    } finally {
      setSaving(false);
    }
  };

  if (state.loading) return <div className="text-sm text-slate-400" data-testid="choices-loading">Chargement…</div>;
  if (state.error) {
    return (
      <div className="text-sm text-slate-500" data-testid="choices-error">
        Impossible de charger vos choix. <button type="button" onClick={load} className="text-jamora hover:underline">Réessayer</button>
      </div>
    );
  }
  return (
    <div data-testid="notification-choices">
      <div className="hidden sm:grid grid-cols-[1fr_5rem_5rem] items-center gap-3 pb-2 text-2xs font-semibold uppercase tracking-wider text-slate-500">
        <span>Quand il se passe quelque chose pour moi</span><span className="text-center">Cloche</span><span className="text-center">WhatsApp</span>
      </div>
      <ul className="divide-y divide-slate-100">
        {state.categories.map((c) => (
          <li key={c.id} className="grid grid-cols-2 sm:grid-cols-[1fr_5rem_5rem] items-center gap-x-3 gap-y-2 py-3" data-testid={`choice-${c.id}`}>
            <span className="col-span-2 sm:col-span-1 text-sm font-medium text-slate-900">{c.label}</span>
            <label className="flex items-center justify-between gap-2 sm:justify-center text-xs text-slate-500">
              <span className="sm:sr-only">Cloche</span>
              <Switch checked={c.bell} onCheckedChange={(v) => change(c.id, "bell", v)} disabled={c.locked || saving} aria-label={`${c.label} : cloche`} data-testid={`choice-${c.id}-bell`} />
            </label>
            <label className="flex items-center justify-between gap-2 sm:justify-center text-xs text-slate-500">
              <span className="sm:sr-only">WhatsApp</span>
              <Switch checked={c.whatsapp && state.whatsappMaster} onCheckedChange={(v) => change(c.id, "whatsapp", v)} disabled={c.locked || saving || !state.whatsappMaster}
                aria-label={`${c.label} : WhatsApp`} data-testid={`choice-${c.id}-whatsapp`} />
            </label>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-slate-500">
        {state.whatsappMaster ? "" : "WhatsApp est désactivé ci-dessus pour tout votre compte. "}
        Les demandes d'assistance adressées aux techniciens et les messages liés à votre compte vous parviennent toujours.
      </p>
    </div>
  );
}
